import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, type LinkProps, type NavLinkProps } from 'react-router-dom';
import { requestedLanguage } from '@shared/locales';

/**
 * Keeps an explicit language choice on internal links.
 *
 * The language switch links to `…?lang=en` (or `?lang=es`). Without help, the
 * next internal link would drop that query, and a later full page load of an
 * unprefixed URL (a reload, a new tab) would be renegotiated from the
 * browser's Accept-Language — sending a Spanish-preferring visitor who chose
 * English straight back to /es. Carrying `?lang=` on internal links keeps the
 * choice in the URL itself: no cookie, no storage.
 *
 * Applied only after hydration, so the server HTML and the first client render
 * agree.
 */
const LanguageQueryContext = createContext('');
const HydratedContext = createContext(false);

export function LanguageQueryProvider({ children }: { children: ReactNode }) {
  const { search } = useLocation();
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const lang = hydrated ? requestedLanguage(search) : null;
  return (
    <HydratedContext.Provider value={hydrated}>
      <LanguageQueryContext.Provider value={lang ? `lang=${lang}` : ''}>{children}</LanguageQueryContext.Provider>
    </HydratedContext.Provider>
  );
}

/** False during the server render and the first client render; true afterwards. */
export function useHydrated(): boolean {
  return useContext(HydratedContext);
}

/** `to` with the current explicit-language query added (keeps any #hash last). */
export function withLanguageQuery(to: string, query: string): string {
  if (!query || /[?&]lang=/.test(to)) return to;
  const hashAt = to.indexOf('#');
  const path = hashAt === -1 ? to : to.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : to.slice(hashAt);
  return `${path}${path.includes('?') ? '&' : '?'}${query}${hash}`;
}

export function useLanguageQuery(): string {
  return useContext(LanguageQueryContext);
}

export function SiteLink({ to, ...props }: Omit<LinkProps, 'to'> & { to: string }) {
  return <Link to={withLanguageQuery(to, useLanguageQuery())} {...props} />;
}

export function SiteNavLink({ to, ...props }: Omit<NavLinkProps, 'to'> & { to: string }) {
  return <NavLink to={withLanguageQuery(to, useLanguageQuery())} {...props} />;
}

/**
 * The same for links inside rendered Markdown (docs and legal pages), which
 * are plain anchors in an HTML string: their hrefs gain the explicit-language
 * query after hydration.
 */
export function useLanguageQueryOnLinks(container: { current: HTMLElement | null }, dependency: unknown): void {
  const query = useLanguageQuery();
  useEffect(() => {
    if (!query || !container.current) return;
    for (const anchor of container.current.querySelectorAll<HTMLAnchorElement>('a[href^="/"]')) {
      const href = anchor.getAttribute('href')!;
      if (!href.startsWith('//')) anchor.setAttribute('href', withLanguageQuery(href, query));
    }
  }, [container, query, dependency]);
}
